import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import {
  searchDoctors,
  selectDoctor,
  clearDoctorSearch,
} from '../store/doctorSlice';
import type { Doctor } from '../services/doctorService';

interface DoctorSelectorProps {
  onSelect?: (doctor: Doctor | null) => void;
  disabled?: boolean;
}

const DoctorSelector: React.FC<DoctorSelectorProps> = ({
  onSelect,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const { searchResults, searchLoading, selectedDoctor } = useAppSelector(
    (state) => state.doctors,
  );

  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input when edit mode opens
  useEffect(() => {
    if (editing) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [editing]);

  // Debounced search
  useEffect(() => {
    if (!editing || !inputValue || inputValue.length < 2) {
      dispatch(clearDoctorSearch());
      return;
    }

    const timer = setTimeout(() => {
      dispatch(searchDoctors({ search: inputValue, limit: 20 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [editing, inputValue, dispatch]);

  const buildDoctorLabel = useCallback((doctor: Doctor): string => {
    let label = doctor.name;
    if (doctor.specialty) {
      label += ` — ${doctor.specialty}`;
    }
    return label;
  }, []);

  const handleSelect = useCallback(
    (_event: React.SyntheticEvent, value: Doctor | null) => {
      dispatch(selectDoctor(value));
      if (value) {
        console.log('DOCTOR_SELECTED | id=%d | name=%s', value.id, value.name);
      }
      onSelect?.(value);
      setEditing(false);
      setInputValue('');
    },
    [dispatch, onSelect],
  );

  const handleClear = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      dispatch(selectDoctor(null));
      setInputValue('');
      onSelect?.(null);
    },
    [dispatch, onSelect],
  );

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setInputValue('');
    setEditing(true);
  }, [disabled]);

  const handleClose = useCallback(() => {
    setEditing(false);
    setInputValue('');
    dispatch(clearDoctorSearch());
  }, [dispatch]);

  // Edit mode — inline Autocomplete, identical visual style to CPTChipCombo
  if (editing) {
    return (
      <ClickAwayListener onClickAway={handleClose}>
        <Box sx={{ width: 280 }}>
          <Autocomplete
            open
            size="small"
            options={searchResults}
            loading={searchLoading}
            inputValue={inputValue}
            onInputChange={(_e, val, reason) => {
              if (reason !== 'reset') setInputValue(val);
            }}
            onChange={handleSelect}
            onClose={(_e, reason) => {
              if (reason === 'escape') handleClose();
            }}
            value={null}
            getOptionLabel={(option) => buildDoctorLabel(option as Doctor)}
            isOptionEqualToValue={(option, value) =>
              (option as Doctor).id === (value as Doctor).id
            }
            filterOptions={(x) => x}
            renderOption={(props, option) => (
              <li {...props} key={(option as Doctor).id}>
                <Box sx={{ py: 0.25 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {(option as Doctor).name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(option as Doctor).specialty ? (option as Doctor).specialty : 'No specialty'}
                    {(option as Doctor).npi ? ` · NPI: ${(option as Doctor).npi}` : ''}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText={
              inputValue.length < 2
                ? 'Type at least 2 characters…'
                : searchLoading
                ? 'Searching…'
                : 'No doctors found'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                placeholder="Doctor name, NPI, or specialty…"
                autoFocus
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <PersonIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 0.5 }} />
                  ),
                  endAdornment: (
                    <>
                      {searchLoading && <CircularProgress color="inherit" size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.78rem',
                    bgcolor: 'background.paper',
                  },
                }}
              />
            )}
            sx={{ width: '100%' }}
          />
        </Box>
      </ClickAwayListener>
    );
  }

  // Selected idle state — chip matching CPTChipCombo selected-exam chip
  if (selectedDoctor) {
    return (
      <Tooltip title={disabled ? '' : 'Click to change doctor · ✕ to clear'} arrow>
        <Chip
          icon={<EditIcon sx={{ fontSize: '0.85rem !important' }} />}
          label={buildDoctorLabel(selectedDoctor)}
          size="small"
          color="success"
          variant="outlined"
          onClick={disabled ? undefined : handleOpen}
          onDelete={disabled ? undefined : handleClear}
          deleteIcon={<ClearIcon fontSize="small" />}
          sx={{ cursor: disabled ? 'default' : 'pointer', maxWidth: 320 }}
          aria-label={`Referring doctor: ${buildDoctorLabel(selectedDoctor)}. Click to change, or click X to clear.`}
        />
      </Tooltip>
    );
  }

  // Empty placeholder — click to search
  return (
    <Tooltip title={disabled ? '' : 'Click to assign a referring doctor'} arrow>
      <Chip
        icon={<PersonIcon sx={{ fontSize: '0.85rem !important', opacity: 0.5 }} />}
        label="Referring doctor…"
        size="small"
        variant="outlined"
        onClick={disabled ? undefined : handleOpen}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          borderStyle: 'dashed',
          color: 'text.secondary',
          borderColor: 'divider',
          maxWidth: 220,
        }}
        aria-label="No referring doctor assigned. Click to search and assign."
      />
    </Tooltip>
  );
};

export default DoctorSelector;
